import React, {
  RefObject,
  useEffect,
} from 'react';
import Table from 'core/components/VirtualTable';
import {
  ReportIcon,
} from 'core/icons';
import Button from 'pages/Dashboard/components/Button';
import {
  useMedia,
  useSidePanel,
  useTableReset,
} from 'core/hooks';
import getRequestedReportList from 'pages/Dashboard/pages/Reports/transport';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  reportEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useSyncDateRange,
} from 'pages/Dashboard/hooks/url';
import DateRangePicker from 'core/components/DateTimePicker/DateRangePicker';
import {
  columns,
} from 'pages/Dashboard/pages/Reports/columns';
import {
  ReportDTO,
} from 'dtos/reportDTO';
import useSignalREffect from 'views/EMR/hooks/signalR';
import {
  buildReportPanelInfo,
  canOpenRun,
  isRunPanelOpen,
  RUN_OUTPUT_PANEL_TYPE,
} from 'views/EMR/components/Panel/RunOutputPanel/utils';
import {
  Row,
} from '@tanstack/react-table';

export default function Reports() {
  const [dateRange, setDateRange] = useSyncDateRange({ uniqueKey: 'reportDateRange' });
  const { reset, tableInstanceRef } = useTableReset();
  const { requestMedia } = useMedia();
  const { activePanel, openPanel } = useSidePanel();

  useSignalREffect('OnReportCompleted', () => {
    tableInstanceRef?.current?.refetch?.();
  });

  useEffect(() => {
    const unsubscribe = reportEventChannel.on('onReportChange', () => {
      tableInstanceRef?.current?.refetch?.();
    });

    return () => { unsubscribe(); };
  }, []);

  return (
    <>
      <div className="flex justify-between items-center pt-4 px-4 mb-4 pb-1">
        <h1 className="font-semibold text-xl">Reports</h1>
      </div>
      <div className="flex justify-between items-center mb-4 px-4">
        <DateRangePicker
          value={dateRange}
          onChange={(nextDateRange) => {
            if (nextDateRange) {
              setDateRange(nextDateRange);
              reset();
            }
          }}
        />
        <Button
          className="!h-8"
          variant="contained"
          type="button"
          color="primary"
          startIcon={<ReportIcon className="!w-4 !h-4" />}
          onClick={() => { reportEventChannel.emit('onReportCreate'); }}
        >
          Run Report
        </Button>
      </div>
      <div className="relative flex flex-col flex-1 overflow-y-hidden">
        <div className="h-full w-full">
          <Table<ReportDTO>
            instanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
            getData={getRequestedReportList}
            getRowId={(row) => row?.reportId}
            queryParams={{ dateRange, requestMedia }}
            columns={columns}
            emptySlot="No Reports Found"
            onRowClick={(_table, row) => {
              if (canOpenRun(row.original.reportState)) {
                openPanel(RUN_OUTPUT_PANEL_TYPE, buildReportPanelInfo(row.original));
              }
            }}
            defaultPagination={{
              pageSize: 25,
              pageIndex: 0,
            }}
            classes={{
              header: 'px-4',
              outerRow: '!w-[calc(100%-16px)] left-4',
              row: (row: Row<ReportDTO>) => (
                isRunPanelOpen(activePanel, { kind: 'report', reportId: row.original.reportId ?? 0 })
                  ? 'bg-blue-50'
                  : ''
              ),
            }}
          />
        </div>
      </div>
    </>
  );
}
